/* ── Choreo Ultimate Burst ────────────────────────────────────────
 * Large radial "burst" pulse at the swarm centroid for ChoreoUltimate.
 * Two concentric expanding spheres: one fast inner rim, one slower
 * outer rim — together they read as a coordinated synchronized strike.
 *
 * Lifecycle scaffolding lives in CardSignatureEffect.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, uv, vec3, vec4, float, smoothstep } from 'three/tsl';
import { setEmissiveNode } from '../tslUtils';
import { CardSignatureEffect, type LivePositionResolver, type Vec3Like } from './CardSignatureEffect';

const LIFE_SECONDS = 1.0;
const PULSE_MAX_RADIUS = 14;
const COLOR_HEX = 0xffe066;

interface ShellHandle {
  mesh: THREE.Mesh;
  geometry: THREE.SphereGeometry;
  material: THREE.MeshBasicNodeMaterial;
  uProgress: ReturnType<typeof uniform>;
  /** Start delay in seconds — outer shell trails the inner. */
  delay: number;
}

interface ChoreoBurstOptions {
  liveResolver?: LivePositionResolver;
}

export class ChoreoBurst extends CardSignatureEffect {
  static readonly LIFE_MS = LIFE_SECONDS * 1000;

  private readonly group: THREE.Group;
  private readonly shells: ShellHandle[] = [];
  private readonly liveResolver?: LivePositionResolver;

  constructor(scene: THREE.Scene, position: Vec3Like, options?: ChoreoBurstOptions) {
    super(scene, LIFE_SECONDS);
    this.group = new THREE.Group();
    this.group.position.set(position.x, position.y, position.z);
    this.liveResolver = options?.liveResolver;

    this.shells.push(this.createShell(0));
    this.shells.push(this.createShell(0.18));

    this.scene.add(this.group);
  }

  private createShell(delay: number): ShellHandle {
    const uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(COLOR_HEX));
    const geometry = new THREE.SphereGeometry(1, 24, 16);
    const material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const colorNode = Fn(() => {
      const u = uv();
      // Equator-bright limb so the shell reads as a thin shockwave, not a filled ball.
      const limb = smoothstep(float(0.0), float(0.3), u.y).mul(
        smoothstep(float(1.0), float(0.7), u.y),
      );
      const fade = float(uProgress).oneMinus();
      const rgb = vec3(uColor).add(vec3(0.4, 0.3, 0.0).mul(limb));
      const alpha = limb.mul(fade).mul(0.7);
      return vec4(rgb, alpha);
    })();
    material.colorNode = colorNode;
    setEmissiveNode(material, colorNode);

    const mesh = new THREE.Mesh(geometry, material);
    mesh.scale.setScalar(0.001);
    this.group.add(mesh);
    return { mesh, geometry, material, uProgress, delay };
  }

  protected tick(): void {
    this.applyLiveAnchor(this.group, this.liveResolver);
    for (const shell of this.shells) {
      const localT = (this.life - shell.delay) / (this.lifeSeconds - shell.delay);
      if (localT <= 0) {
        shell.mesh.scale.setScalar(0.001);
        shell.uProgress.value = 0;
        continue;
      }
      const clamped = Math.min(1, localT);
      const eased = 1 - (1 - clamped) * (1 - clamped);
      shell.mesh.scale.setScalar(eased * PULSE_MAX_RADIUS);
      shell.uProgress.value = clamped;
    }
  }

  protected disposeResources(): void {
    this.scene.remove(this.group);
    for (const shell of this.shells) {
      shell.geometry.dispose();
      shell.material.dispose();
    }
    this.shells.length = 0;
  }
}
